import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  BookingItem,
  BookingRequest,
  tentTypeToName,
  TentTypeV1,
  TentTypeV2,
} from '@camp67/model';
import { formatDate } from 'date-fns/format';
import { hasValidDates, parseDate } from '@camp67/model/booking/dates';
import { displayPrice, exchangeRate } from '../../state/prices';
import { Tooltip } from '../tooltip';
import { Divider } from '../divider';
import { MetadataWithIcon, MetadataIconType } from '../metadata-with-icon';

export type { MetadataIconType };

export interface SubmissionCTAMessages {
  total: string;
  room: string;
  rooms: string;
  night: string;
  nights: string;
  empty: string;
  continueButton: string;
  priceShownIn: string;
  exchangeCalculated: string;
}

export interface SubmissionCTAProps {
  total: number;
  totalWithGst: number;
  totalGst: number;
  /** GST percentage (e.g., 5 for 5%) */
  gstPercentage: number;
  nights: number;
  hasErrors: boolean;
  canSubmit: boolean;
  onClick: VoidFunction;
  reservation: BookingRequest['reservation'];
  messages: SubmissionCTAMessages;
  /** Optional logo image URL */
  logoUrl?: string;
  logoAlt?: string;
  /** Optional analytics event handler */
  onAnalyticsEvent?: (event: string, data?: Record<string, unknown>) => void;
  /** Function to get tent config for gallery display */
  getTentConfig?: (type: TentTypeV1 | TentTypeV2) => {
    imageUrl: string;
    imageAlt: string;
    title: string;
  } | null;
  /** Optional custom icon renderer for metadata icons */
  renderIcon?: (icon: MetadataIconType) => React.ReactNode;
  /** Custom CSS class for the CTA button background (e.g., 'bg-emerald-600' for KS) */
  ctaButtonClassName?: string;
  /** Custom CSS class for the CTA button shadow */
  ctaShadowClassName?: string;
}

export const SubmissionCTA: React.FC<SubmissionCTAProps> = (props) => (
  <div className="w-full">
    <div className="md:hidden">
      <SubmissionCTAMobile {...props} />
    </div>
    <div className="hidden md:block">
      <SubmissionCTADesktop {...props} />
    </div>
  </div>
);

export const SubmissionCTAMobile: React.FC<SubmissionCTAProps> = ({
  total,
  totalWithGst,
  nights,
  canSubmit,
  reservation,
  onClick,
  messages,
  ctaButtonClassName = 'bg-red-200',
}) => {
  const rooms = reservation.rooms;
  const totalRooms = rooms.reduce((sum, [, c]) => sum + c, 0);

  const isValidRange = hasValidDates(reservation.startDate, reservation.endDate);

  const noStartDate = !reservation.startDate;
  const noEndDate = !reservation.endDate;
  const startDate =
    nights && !noStartDate
      ? formatDate(parseDate(reservation.startDate), 'dd')
      : undefined;
  const endDate =
    nights && !noEndDate
      ? formatDate(parseDate(reservation.endDate), 'dd MMM')
      : undefined;

  const totalPriceWithGST = displayPrice({
    priceInINR: totalWithGst,
  });

  const roomsLabel = totalRooms === 1 ? messages.room : messages.rooms;

  return (
    <div
      className={
        'w-full bg-white flex fixed z-[2000] bottom-0 left-0 shadow-top pl-5 h-14 items-center'
      }
    >
      {nights && isValidRange ? (
        <div>
          {total && totalRooms > 0 ? (
            <p className="font-mono font-bold text-sm">
              {totalPriceWithGST}&nbsp;
              {messages.total.toLocaleLowerCase()}
            </p>
          ) : (
            ''
          )}
          <p className="font-sans text-gray-500 text-sm">
            {startDate} - {endDate}{' '}
            {total && totalRooms > 0
              ? `(${totalRooms} ${roomsLabel.toLocaleLowerCase()})`
              : ''}
          </p>
        </div>
      ) : (
        <p className="font-sans py-2 text-sm">{messages.empty}</p>
      )}

      <button
        className={clsx(
          'text-white whitespace-nowrap flex flex-col flex-1 items-center justify-center py-2 px-4 ml-4 h-14',
          ctaButtonClassName,
          {
            'cursor-pointer': canSubmit,
            'opacity-50 cursor-not-allowed pointer-events-none': !canSubmit,
          },
        )}
        type="button"
        disabled={!canSubmit}
        onClick={onClick}
      >
        <p className="font-sans uppercase text-xs xs:text-sm md:text-base font-bold tracking-widest text-left w-full">
          {messages.continueButton} &rarr;
        </p>
      </button>
    </div>
  );
};

export const SubmissionCTADesktop: React.FC<SubmissionCTAProps> = ({
  total,
  totalWithGst,
  totalGst,
  gstPercentage,
  nights,
  canSubmit,
  reservation,
  onClick,
  messages,
  logoUrl,
  logoAlt,
  getTentConfig,
  renderIcon,
  ctaButtonClassName = 'bg-red-200',
  ctaShadowClassName = 'shadow-red-400',
}) => {
  const rooms = reservation.rooms;
  const totalRooms = rooms.reduce((sum, [, c]) => sum + c, 0);

  const isValidRange = hasValidDates(reservation.startDate, reservation.endDate);

  const noStartDate = !reservation.startDate;
  const noEndDate = !reservation.endDate;
  const startDate =
    nights && !noStartDate
      ? formatDate(parseDate(reservation.startDate), 'dd MMM')
      : undefined;
  const endDate =
    nights && !noEndDate
      ? formatDate(parseDate(reservation.endDate), 'dd MMM')
      : undefined;

  const totalPrice = displayPrice({ priceInINR: total });
  const totalPriceWithGST = displayPrice({
    priceInINR: totalWithGst,
  });
  const totalPriceGst = displayPrice({ priceInINR: totalGst });

  const nightsLabel = nights === 1 ? messages.night : messages.nights;
  const roomsLabel = totalRooms === 1 ? messages.room : messages.rooms;

  return (
    <div
      className={
        'w-full md:mt-9 flex md:block sm:pt-6 bg-white text-center fixed z-[2000] md:z-10 bottom-0 left-0 shadow-top md:shadow-none md:sticky md:top-0 self-start rounded-lg'
      }
    >
      {logoUrl && (
        <div className="hidden md:flex flex-row items-center justify-center w-full">
          <Divider className="border-dashed border-gray-300" />
          <img
            src={logoUrl}
            alt={logoAlt ?? 'Logo'}
            className="min-w-16 mx-4 pointer-events-none select-none"
          />
          <Divider className="border-dashed border-gray-300" />
        </div>
      )}
      {nights && isValidRange ? (
        <>
          <p className="font-sans mt-3 px-12 text-gray-500">
            {startDate} - {endDate}
          </p>
          <div className="flex flex-row items-center gap-5 mt-1.5 justify-center">
            <MetadataWithIcon
              text={`${nights} ${nightsLabel}`.toLocaleLowerCase()}
              icon="nights"
              renderIcon={renderIcon}
            />
            <MetadataWithIcon
              text={`${totalRooms} ${roomsLabel.toLocaleLowerCase()}`}
              icon="room"
              renderIcon={renderIcon}
            />
          </div>
        </>
      ) : (
        <p className="font-sans mt-3 px-2">{messages.empty}</p>
      )}
      {totalRooms > 0 ? (
        <SubmissionCTAGallery rooms={rooms} getTentConfig={getTentConfig} />
      ) : null}
      {total && totalRooms > 0 ? (
        <>
          <hgroup>
            <p className="font-sans uppercase text-gray-500 mt-5 text-sm tracking-wider">
              {messages.total}
            </p>
            <Tooltip
              text={
                <>
                  {messages.priceShownIn} {exchangeRate.currency}.{' '}
                  {exchangeRate.currency !== 'INR' ? (
                    <>
                      {messages.exchangeCalculated} ({exchangeRate.rate.toFixed(2)} INR =
                      1 {exchangeRate.currency}).
                    </>
                  ) : null}
                </>
              }
              spacing="tall"
            >
              <h3 className="font-mono text-2xl md:text-3xl font-medium">
                {totalPriceWithGST}
              </h3>
            </Tooltip>
          </hgroup>
          <Divider className="hidden md:block border-dashed border-gray-300 my-5" />
          <ul className="hidden md:block font-mono max-w-[80%] w-120 m-auto px-2">
            <li className="flex flex-row items-center w-full gap-4 justify-center whitespace-nowrap text-nowrap">
              <p>{messages.room}</p>
              <Divider className="border-dotted border-gray-400" />
              <p>{totalPrice}</p>
            </li>
            <ul className="text-left text-xs hidden md:flex flex-col w-full text-gray-500 mt-1 m-auto">
              {rooms.map(([r, c], index) => (
                <li key={index} className="m-0.5 break-words">
                  {c} x {tentTypeToName[r]}, {nights} {nightsLabel.toLocaleLowerCase()}
                </li>
              ))}
            </ul>
            <li className="flex flex-row items-center w-full gap-4 justify-center mt-3">
              <p>GST ({gstPercentage}%)</p>
              <Divider className="border-dotted border-gray-400" />
              <p>{totalPriceGst}</p>
            </li>
          </ul>
        </>
      ) : null}
      <button
        className={clsx(
          'w-full text-white mt-5 flex flex-col items-center py-6 px-6 rounded-md shadow-mdy',
          ctaButtonClassName,
          ctaShadowClassName,
          {
            'cursor-pointer': canSubmit,
            'opacity-50 cursor-not-allowed pointer-events-none': !canSubmit,
          },
        )}
        type="button"
        disabled={!canSubmit}
        onClick={onClick}
      >
        <p className="font-sans uppercase text-sm md:text-base font-bold tracking-widest">
          {messages.continueButton} &rarr;
        </p>
      </button>
    </div>
  );
};

interface SubmissionCTAGalleryProps {
  rooms: BookingItem[];
  showOnMobile?: boolean;
  showLabels?: boolean;
  onClickRoom?: (room: TentTypeV1 | TentTypeV2) => void;
  onAnalyticsEvent?: (event: string, data?: Record<string, unknown>) => void;
  /** Function to get tent config by type */
  getTentConfig?: (type: TentTypeV1 | TentTypeV2) => {
    imageUrl: string;
    imageAlt: string;
    title: string;
  } | null;
}

export const SubmissionCTAGallery: React.FC<SubmissionCTAGalleryProps> = ({
  rooms,
  showOnMobile = false,
  showLabels = false,
  onClickRoom,
  onAnalyticsEvent,
  getTentConfig,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const totalRooms = rooms.length;

  const onNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % totalRooms);
    onAnalyticsEvent?.('button_click', { buttonTag: 'next_image' });
  }, [onAnalyticsEvent, totalRooms]);

  const onPrev = useCallback(() => {
    setActiveIndex((prev) => Math.max(0, prev - 1));
    onAnalyticsEvent?.('button_click', { buttonTag: 'prev_image' });
  }, [onAnalyticsEvent]);

  const activeRoom = useMemo(() => {
    if (totalRooms === 0 || !getTentConfig) return null;
    return getTentConfig(rooms[activeIndex][0]);
  }, [activeIndex, rooms, totalRooms, getTentConfig]);

  if (totalRooms === 0 || !activeRoom) {
    return null;
  }

  return (
    <div className="w-full h-full relative mt-4">
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
      <img
        className={clsx(
          'md:flex w-full h-full object-cover pointer-events-none select-none',
          {
            hidden: !showOnMobile,
          },
        )}
        src={activeRoom.imageUrl}
        alt={activeRoom.imageAlt}
        onClick={() => onClickRoom?.(rooms[activeIndex][0])}
      />
      {showLabels ? (
        <span className="bg-white absolute left-4 bottom-4 px-2 py-1 rounded-sm text-xs uppercase tracking-wider font-medium">
          {activeRoom.title}
        </span>
      ) : null}
      {totalRooms > 1 ? (
        <>
          <button
            onClick={onPrev}
            type="button"
            className={clsx(
              'min-w-10 w-10 h-10 min-h-10 rounded-full bg-white absolute top-1/2 -translate-y-1/2 left-6 flex-col items-center justify-center',
              {
                hidden: !showOnMobile,
                flex: showOnMobile,
              },
            )}
          >
            <svg
              width="12"
              height="21"
              viewBox="0 0 12 21"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="10 2 2 10.5 10 19" />
            </svg>
          </button>
          <button
            onClick={onNext}
            type="button"
            className={clsx(
              'min-w-10 w-10 h-10 min-h-10 rounded-full bg-white absolute top-1/2 -translate-y-1/2 right-4 flex-col items-center justify-center',
              {
                hidden: !showOnMobile,
                flex: showOnMobile,
              },
            )}
          >
            <svg
              width="12"
              height="21"
              viewBox="0 0 12 21"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="2 2 10 10.5 2 19" />
            </svg>
          </button>
        </>
      ) : null}
    </div>
  );
};
